import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { comments } from '@/mocks/comments';

const categoryNameMap: Record<string, string> = {
  'mingxing-fanche': '明星翻车',
  'wanghong-tafang': '网红塌房',
  'qiye-neimu': '企业内幕',
  'shishi-baoliao': '实时爆料',
  'heiliao-baoguang': '黑料曝光',
};

function formatViews(views: number) {
  if (views >= 100000) return `${(views / 10000).toFixed(0)}万阅`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万阅`;
  return `${views}阅`;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center text-foreground-400 mb-4">
              <i className="ri-article-line text-4xl"></i>
            </div>
            <p className="text-lg text-foreground-700">文章不存在或已被删除</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const catName = categoryNameMap[article.category] || article.category;
  const articleComments = comments.filter((c) => c.articleId === article.id);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: catName, path: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    url: `https://www.51chigua.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '51吃瓜每日大赛',
      url: 'https://www.51chigua.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.51chigua.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.51chigua.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: catName,
        item: `https://www.51chigua.com/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `https://www.51chigua.com/article/${article.slug}`,
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${article.title} - 51吃瓜每日大赛`,
    description: article.excerpt,
    url: `https://www.51chigua.com/article/${article.slug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <Navbar />

      <section className="relative w-full h-[350px] md:h-[480px] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          title={`${article.title} - 51吃瓜每日大赛`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        <div className="relative z-10 h-full max-w-4xl mx-auto px-4 md:px-6 flex flex-col justify-end pb-10">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <h1 className="text-2xl md:text-4xl font-heading font-black text-white leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
            <span className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full overflow-hidden bg-foreground-300">
                <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
              </div>
              {article.author}
            </span>
            <span>{article.date}</span>
            <span>{formatViews(article.views)} 阅读</span>
            <Link
              to={`/category/${article.category}`}
              className="px-3 py-1 bg-white/20 text-white rounded-full text-xs hover:bg-white/30 transition-colors"
            >
              {catName}
            </Link>
          </div>
        </div>
      </section>

      <article className="py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="bg-background-50 rounded-2xl p-6 md:p-10 mb-10">
            <p className="text-lg text-foreground-700 leading-relaxed font-label border-l-4 border-primary-500 pl-5 mb-8">
              {article.excerpt}
            </p>
            <div
              className="prose prose-lg max-w-none text-foreground-800"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1.5 bg-accent-100 text-accent-700 text-xs font-label rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </article>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="mb-10">
            <span className="inline-block px-3 py-1 text-xs font-label font-medium bg-accent-100 text-accent-700 rounded-full mb-4">
              瓜友热评
            </span>
            <h2 className="text-2xl font-heading font-bold text-foreground-900">
              评论 ({articleComments.length})
            </h2>
          </div>
          {articleComments.length === 0 ? (
            <p className="text-foreground-500 text-sm">暂无评论，来做第一个吃瓜人吧</p>
          ) : (
            <div className="flex flex-col gap-4">
              {articleComments.map((comment) => (
                <div key={comment.id} className="bg-background-50 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img src={comment.avatar} alt={comment.username} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="text-sm font-label font-medium text-foreground-900">{comment.username}</div>
                      <div className="text-xs text-foreground-500">@{comment.username} · {comment.date}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
                    <button className="flex items-center gap-1 hover:text-primary-500 transition-colors">
                      <i className="ri-thumb-up-line"></i> {comment.likes}
                    </button>
                    <button className="flex items-center gap-1 hover:text-primary-500 transition-colors">
                      <i className="ri-chat-1-line"></i> 回复
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {relatedArticles.length > 0 && (
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <h2 className="text-2xl font-heading font-bold text-foreground-900">相关推荐</h2>
              <p className="text-sm text-foreground-500 mt-1">同分类下的更多精彩内容</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group relative rounded-2xl overflow-hidden aspect-[4/5] bg-foreground-100"
                >
                  <img
                    src={ra.image}
                    alt={ra.title}
                    title={`${ra.title} - 51吃瓜每日大赛`}
                    className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-sm font-heading font-bold text-white leading-snug line-clamp-2 group-hover:text-primary-300 transition-colors">
                      {ra.title}
                    </h3>
                    <div className="mt-2 text-xs text-white/60">{formatViews(ra.views)} · {ra.date}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}